<template>
  <main class="main-content">

    <InfoPopup v-model="showInfoPopup" :title="infoTitle" :showCancelButton="showInfoPopupCancelButton">
      <div class="info-message">
        <p>{{ infoMessage }}</p>
        <ul v-if="infoDetails.length" class="info-detail-list">
          <li v-for="detail in infoDetails" :key="detail">{{ detail }}</li>
        </ul>
      </div>
    </InfoPopup>

    <InfoPopup v-model="showImportConfirmPopup" title="Import Text File" @confirm="confirmImportFile"
      @cancel="clearPendingImport">
      <div class="import-confirmation">
        <p>Import {{ pendingImportFileName }} content to this page?</p>
        <label class="import-empty-line-option">
          <input type="checkbox" v-model="ignoreEmptyImportLines" />
          <span>Ignore empty lines?</span>
        </label>
      </div>
    </InfoPopup>

    <UnsupportedCharacterPopup v-model="showUnsupportedCharacterPopup" :title="unsupportedCharacterTitle"
      :message="unsupportedCharacterMessage" :summary="unsupportedCharacterSummary" :details="unsupportedCharacterDetails"
      :reviewSections="unsupportedCharacterReviewSections"
      :confirmText="unsupportedCharacterConfirmText" :cancelText="unsupportedCharacterCancelText"
      @confirm="confirmUnsupportedCharacterAction" @cancel="clearPendingUnsupportedCharacterAction" />

    <div class="content-card">
      <div class="header">
        <div>
          <h3>Text-to-Speech Studio</h3>
        </div>
      </div>
      <div class="panel-toggle-bar">
        <button class="toggle-btn export-btn" @click="goToProjects">Switch Projects</button>
        <label class="panel-toggle-item">
          <input type="checkbox" name="panel" v-model="showProjectPanel" />
          <span class="slider"></span>
          <span class="switch-label">
            Project Panel
          </span>
        </label>
        <label class="panel-toggle-item">
          <input type="checkbox" name="panel" v-model="showSettingsPanel" />
          <span class="slider"></span>
          <span class="switch-label">
            Settings Panel
          </span>
        </label>
        <div class="spacer"></div>
        <button class="toggle-btn import-btn" @click="triggerImport">Import</button>
        <input ref="importInput" type="file" accept=".txt" style="display:none" aria-label="Import .txt file"
          @change="onImportFile" />
        <button class="toggle-btn export-btn" @click="openExportDialog">Export</button>
        <button class="credit-meter"> Credit: <div class="credit-count">{{
          balance.toLocaleString() }}</div></button>
      </div>
      <div class="panels-container" :style="gridStyle">
        <div class="project-panel" v-show="showProjectPanel">
          <ProjectPanel ref="projectPanel" :project="project" @select="selectPage" @select-name="selectPage" />
        </div>

        <div class="editor-panel">
          <div v-if="isLoadingLines" class="loading-overlay">
            <div class="spinner"></div>
          </div>
          <div class="editor-panel-inner" :class="{ disabled: isLoadingLines }">
            <EditorPanel v-model:lines="pageLines" :project="project" :pageId="selectedPage" :saveStatus="saveStatus"
              @select-line="onLineSelect" @play-line="onLinePlay" @play-all-lines="onPlayAllLines"
              :isGenerating="isEditorLineGenerating" :generatingLineIndex="generatingLineIndex"
              :reloadAll="reloadAll" @page-changed="onPageChanged" />
          </div>
        </div>
        <div class="settings-history-panel" v-show="showSettingsPanel">
          <div class="settings-history-tabs">
            <button class="tab-btn" :class="{ active: activeTab === 'Settings' }" @click="activeTab = 'Settings'">
              Settings
            </button>
            <button class="tab-btn" :class="{ active: activeTab === 'History' }"
              @click="activeTab = 'History'">History</button>
          </div>
          <SettingsPanel v-if="activeTab === 'Settings'" v-model:model="model"
            v-model:settingsConfig="settingsConfig" />
          <HistoryPanel v-else v-model:generatedAudio="generatedAudio" />
        </div>
      </div>

      <div v-if="showExport" class="modal-backdrop" @click.self="closeExport">
        <div class="modal-window">
          <div class="modal-header">
            <h3>Export Page Audio</h3>
            <button class="modal-close" @click="closeExport" aria-label="Close">×</button>
          </div>
          <div class="modal-body">
            <div class="modal-warning">
              <span class="warn-icon">!</span>
              Export will consume credits and may take some time. Please review your lines before proceeding.
            </div>
            <p>This will combine all lines on this page into a single audio file.</p>
            <ul class="modal-list">
              <li>Lines marked <span class="modal-audio-tag" aria-label="Audio tag"><span>Audio</span><span
                    class="modal-audio-remove" aria-hidden="true"><svg viewBox="0 0 16 16" fill="none"
                      stroke="currentColor" stroke-width="2" stroke-linecap="round">
                      <path d="M4 4l8 8M12 4l-8 8" />
                    </svg></span></span> already have saved audio and will be reused.</li>
              <li>Lines without saved audio will be generated during export.</li>
              <li>If a line has characters our voice engine cannot read, those characters <strong>will be skipped</strong> during export.</li>
              <li>Exporting may take some time and credits, depending on text length and the selected model.</li>
              <li>To regenerate a saved line, click the small x inside its <span class="modal-audio-tag"
                  aria-label="Audio tag"><span>Audio</span><span class="modal-audio-remove" aria-hidden="true"><svg
                      viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                      <path d="M4 4l8 8M12 4l-8 8" />
                    </svg></span></span> before exporting.</li>
            </ul>
          </div>
          <div class="modal-actions">
            <button class="btn btn-primary" @click="startExport" :disabled="isExporting">
              <span v-if="!isExporting">Start Export</span>
              <div v-else class="button-spinner"></div>
            </button>
            <button class="btn btn-secondary" @click="closeExport">Cancel</button>
          </div>
        </div>
      </div>
    </div>
    <div class="audio-player-wrapper"
      :class="{ disabled: isBottomPlayerDisabled && !isLoadingSelectedLineAudio, loading: isLoadingSelectedLineAudio }"
      :aria-busy="isLoadingSelectedLineAudio ? 'true' : 'false'">
      <div v-if="isLoadingSelectedLineAudio" class="audio-loading-state" role="status" aria-live="polite">
        <span class="audio-loading-spinner" aria-hidden="true"></span>
        <span>Loading saved audio...</span>
      </div>
      <template v-else>
        <div class="audio-player-area">
          <audio ref="audioPlayer" controls :src="generatedAudio" :muted="isBottomPlayerDisabled"
            :aria-disabled="isBottomPlayerDisabled ? 'true' : 'false'"
            :tabindex="isBottomPlayerDisabled ? -1 : 0"></audio>
        </div>
        <button class="btn btn-download" @click="downloadAudio" :disabled="isBottomPlayerDisabled">Download</button>
      </template>
    </div>
  </main>
</template>

<script>
import { useRouter } from 'vue-router';
import { provide, ref, reactive, watch, onMounted, onBeforeUnmount, nextTick, computed } from 'vue';
import { requireAuth, checkAuthentication } from '@/api/auth';
import { getLinesByUserId, updateLinesByPage, updateLineByUserId } from '@/api/ttsProjects';
import { downloadFile } from '@/api/files';
import {
  postNewTTSRequest,
  postNewTTSStreamRequest,
  postNewTTSChunkStreamByVoiceRequest,
  getTTSRequestByUploadId
} from '@/api/services'
import { checkTTSPayload } from '@/utils/TTSPayloadChecker'
import { formatTTSPayload } from '@/utils/TTSPayloadFormater'
import {
  findUnsupportedCharacterIssues,
  formatUnsupportedCharacterIssueDetail,
  formatUnsupportedLineList,
  removeUnsupportedTtsCharacters,
  summarizeMostCommonUnsupportedCharacters
} from '@/utils/TTSUnsupportedCharacters';
import {
  findLongProLineIssues,
  formatLongProLineIssueDetail
} from '@/utils/TTSInputWarnings';
import ProjectPanel from '@/components/TTS/ProjectPanel.vue';
import SettingsPanel from '@/components/TTS/SettingsPanel.vue';
import HistoryPanel from '@/components/TTS/HistoryPanel.vue';
import EditorPanel from '@/components/TTS/EditorPanel.vue'
import UnsupportedCharacterPopup from '@/components/TTS/UnsupportedCharacterPopup.vue';
import { getCreditBalance } from '@/api/credits';
import InfoPopup from '@/components/common/InfoPopup.vue';
export default {
  name: 'TTSView',
  components: {
    ProjectPanel, SettingsPanel, HistoryPanel, EditorPanel, UnsupportedCharacterPopup, InfoPopup,
  },
  setup() {
    const router = useRouter();
    const ttsSharedData = ref({})
    provide('ttsSharedData', ttsSharedData)
    const activeTab = ref('Settings');
    const defaultModel = ref("Kokoro v1.0")
    const model = ref("Kokoro v1.0")
    const defaultConfig = ref({
      'Kokoro v1.0': {
        'voice_weights': {
          "am_adam": 1
        },
        "speed": 1.0,
      },
      'IndexTTS2': {
        'voice_name': '',
        'voice_id': '',
        'emo_random': false,
        'max_text_tokens_per_sentence': 120,
      },
      'CosyVoice2-0.5B': {
        'instruct_text': '',
        'audio_file_id': '',
        'voice_instruct_text': '',
        'voice_name': ''
      }
    });
    const settingsConfig = ref({
      'voice_weights': {
        "am_adam": 1
      },
      "speed": 1.0,
    });
    const showInfoPopup = ref(false);
    const infoTitle = ref('');
    const infoMessage = ref('');
    const infoDetails = ref([]);
    const showInfoPopupCancelButton = ref(true);
    const showImportConfirmPopup = ref(false);
    const pendingImportFile = ref(null);
    const pendingImportFileName = ref('');
    const ignoreEmptyImportLines = ref(true);
    const showUnsupportedCharacterPopup = ref(false);
    const unsupportedCharacterTitle = ref('Unsupported Characters Found');
    const unsupportedCharacterMessage = ref('');
    const unsupportedCharacterSummary = ref('');
    const unsupportedCharacterReviewSections = ref([]);
    const unsupportedCharacterDetails = ref([]);
    const unsupportedCharacterConfirmText = ref('Generate without those characters');
    const unsupportedCharacterCancelText = ref('Cancel generation');
    const pendingUnsupportedCharacterAction = ref(null);
    const pendingUnsupportedCharacterLineIndex = ref(null);

    const selectedPage = ref(0);
    const selectedPageName = ref('');
    const project = reactive({ project_id: null, project_name: '' });

    const projectPanel = ref(null);
    const audioPlayer = ref(null);
    const editorContent = ref('');
    const editorSelectedLine = ref('');
    const editorSelectedLineIndex = ref(0);
    const saveStatus = ref('Saved');
    const generatedAudio = ref('');
    const bottomPlayerAudioId = ref(null);
    const isLoadingSelectedLineAudio = ref(false);
    const isGenerating = ref(false);
    const generatingLineIndex = ref(null);
    const generatingPageId = ref(null);
    const isExporting = ref(false);
    let autosaveTimer = null;
    let generationRequestId = 0;
    let selectedLineAudioRequestId = 0;
    let generatedAudioObjectUrl = '';
    const TTS_GENERATION_TIMEOUT_MS = 180000;
    const TXT_FILE_EXTENSION = /\.txt$/i;
    const TTS_LINE_AUDIO_CACHE_NAME = 'timbrely-tts-line-audio-v1';
    const TTS_LINE_AUDIO_MEMORY_LIMIT = 30;
    const TTS_PAGE_PLAYBACK_AUDIO_ID = '__tts_page_playback__';
    const lineAudioBlobCache = new Map();

    const showProjectPanel = ref(true);
    const showSettingsPanel = ref(true);
    const showExport = ref(false);
    const pageLines = ref([]);
    const audioFileIds = ref([]);

    const importInput = ref(null);
    const isLoadingLines = ref(false);

    const balance = ref(1);
    provide('pageLines', pageLines);
    provide('editorSelectedLineIndex', editorSelectedLineIndex);
    const isEditorLineGenerating = computed(() =>
      isGenerating.value
      && !isExporting.value
      && generatingPageId.value === selectedPage.value
      && generatingLineIndex.value !== null
    );
    const selectedLineAudioId = computed(() =>
      getCleanAudioId(pageLines.value[editorSelectedLineIndex.value]?.audio_file_id)
    );
    const isBottomPlayerDisabled = computed(() => {
      if (bottomPlayerAudioId.value === TTS_PAGE_PLAYBACK_AUDIO_ID) {
        return !generatedAudio.value || isLoadingSelectedLineAudio.value;
      }
      return !selectedLineAudioId.value
        || bottomPlayerAudioId.value !== selectedLineAudioId.value
        || !generatedAudio.value
        || isLoadingSelectedLineAudio.value;
    });
    function triggerImport() {
      if (!importInput.value) return;
      importInput.value.value = '';
      importInput.value.click();
    }
    function goToProjects() {
      router.push({
        path: '/text-to-speech-projects',
      });
    }
    function onImportFile(event) {
      const file = event.target.files?.[0];
      if (!file) return;
      if (!TXT_FILE_EXTENSION.test(file.name)) {
        showError(
          'Unsupported file type',
          'Please choose a .txt file. TTS Studio currently imports text files only.'
        );
        event.target.value = '';
        return;
      }
      pendingImportFile.value = file;
      pendingImportFileName.value = file.name;
      ignoreEmptyImportLines.value = true;
      showImportConfirmPopup.value = true;
      event.target.value = '';
    }

    function readTextFile(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target?.result ?? '');
        reader.onerror = () => reject(new Error('We could not read this .txt file. Please try again with another text file.'));
        reader.readAsText(file);
      });
    }

    function createImportedLine(line) {
      return {
        page_id: -1,
        text: line.trim() === '' ? 'NULL' : line,
        audio_file_id: -1,
        voice_setting: JSON.stringify(defaultConfig.value['Kokoro v1.0']),
        status: 'created'
      };
    }

    function clearPendingImport() {
      pendingImportFile.value = null;
      pendingImportFileName.value = '';
      ignoreEmptyImportLines.value = true;
    }

    async function confirmImportFile() {
      const file = pendingImportFile.value;
      const shouldIgnoreEmptyLines = ignoreEmptyImportLines.value;
      if (!file) return;
      try {
        const text = await readTextFile(file);
        const importedLines = String(text)
          .split(/\r?\n/)
          .filter(line => !(shouldIgnoreEmptyLines && line.trim() === ''))
          .map(createImportedLine);

        if (importedLines.length === 0) {
          showError('No lines imported', 'This .txt file does not contain any non-empty lines.');
          return;
        }

        pageLines.value.push(...importedLines);
        await saveLines();
      } catch (err) {
        showError('Import failed', err?.message || 'We could not read this .txt file. Please try again with another text file.');
      } finally {
        clearPendingImport();
      }
    }
    function onLineSelect(idx) {
      editorSelectedLineIndex.value = idx;
      editorSelectedLine.value = pageLines.value[idx]?.text || '';
      loadSelectedLineAudio();
    }

    async function onLinePlay(idx) {
      editorSelectedLineIndex.value = idx;
      editorSelectedLine.value = pageLines.value[idx]?.text || '';

      const l = pageLines.value[idx];
      if (l.text.trim() == 'NULL') return;
      if (l.text.trim() == '') return;
      if (l.audio_file_id != -1) {
        await loadSelectedLineAudio({ autoplay: true });
        return;
      }
      await generateSpeech();
    }
    function closeExport() {
      showExport.value = false;
    }
    async function loadBalance() {
      if (!await requireAuth()) { return }
      try {
        balance.value = await getCreditBalance();
      } catch (err) {
        console.warn('Failed to load credit balance:', err);
        balance.value = null;
      }
    }

    loadBalance();

    function modelName(value = model.value) {
      return String(value || '').toLowerCase();
    }

    function isKokoroModel(value = model.value) {
      return modelName(value).includes('kokoro');
    }

    function isIndexTTS2Model(value = model.value) {
      return modelName(value).includes('indextts2');
    }

    function isCosyVoiceModel(value = model.value) {
      return modelName(value).includes('cosyvoice');
    }

    function isStreamingModel(value = model.value) {
      return isKokoroModel(value) || isIndexTTS2Model(value);
    }

    function getDefaultConfigForModel(value = model.value) {
      if (defaultConfig.value[value]) return defaultConfig.value[value];
      if (isKokoroModel(value)) return defaultConfig.value['Kokoro v1.0'];
      if (isIndexTTS2Model(value)) return defaultConfig.value['IndexTTS2'];
      if (isCosyVoiceModel(value)) return defaultConfig.value['CosyVoice2-0.5B'];
      return {};
    }

    function sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    }

    function withTimeout(promise, ms, message) {
      let timeoutId;
      const timeoutPromise = new Promise((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error(message)), ms);
      });
      return Promise.race([promise, timeoutPromise]).finally(() => clearTimeout(timeoutId));
    }

    function clonePlain(value) {
      if (!value || typeof value !== 'object') return value;
      try {
        return JSON.parse(JSON.stringify(value));
      } catch (_err) {
        return { ...value };
      }
    }

    function concatUint8Arrays(chunks) {
      const totalLength = chunks.reduce((sum, chunk) => sum + chunk.byteLength, 0);
      const out = new Uint8Array(totalLength);
      let offset = 0;
      chunks.forEach(chunk => {
        out.set(chunk, offset);
        offset += chunk.byteLength;
      });
      return out;
    }

    function pcm16ToWavArrayBuffer(pcmBuffer, sampleRate = 24000, channels = 1) {
      const pcm = pcmBuffer instanceof Uint8Array ? pcmBuffer : new Uint8Array(pcmBuffer);
      const bytesPerSample = 2;
      const blockAlign = channels * bytesPerSample;
      const byteRate = sampleRate * blockAlign;
      const wavBuffer = new ArrayBuffer(44 + pcm.byteLength);
      const view = new DataView(wavBuffer);

      function writeString(offset, value) {
        for (let i = 0; i < value.length; i++) {
          view.setUint8(offset + i, value.charCodeAt(i));
        }
      }

      writeString(0, 'RIFF');
      view.setUint32(4, 36 + pcm.byteLength, true);
      writeString(8, 'WAVE');
      writeString(12, 'fmt ');
      view.setUint32(16, 16, true);
      view.setUint16(20, 1, true);
      view.setUint16(22, channels, true);
      view.setUint32(24, sampleRate, true);
      view.setUint32(28, byteRate, true);
      view.setUint16(32, blockAlign, true);
      view.setUint16(34, 16, true);
      writeString(36, 'data');
      view.setUint32(40, pcm.byteLength, true);
      new Uint8Array(wavBuffer, 44).set(pcm);
      return wavBuffer;
    }

    function extractAudioPayloadFromFrames(buffer) {
      const view = new DataView(buffer);
      const chunks = [];
      let offset = 0;
      const headerSize = 12;
      while (offset + headerSize <= view.byteLength) {
        const frameType = view.getUint8(offset + 2);
        const payloadLength = view.getUint32(offset + 8, false);
        const frameEnd = offset + headerSize + payloadLength;
        if (frameEnd > view.byteLength) break;
        if (frameType === 0 && payloadLength > 0) {
          chunks.push(new Uint8Array(buffer.slice(offset + headerSize, frameEnd)));
        }
        offset = frameEnd;
      }
      return concatUint8Arrays(chunks);
    }

    async function responseErrorMessage(response) {
      try {
        const text = await response.text();
        return text.replace(/^"|"$/g, '') || `HTTP ${response.status}`;
      } catch (_err) {
        return `HTTP ${response.status}`;
      }
    }

    async function responseToWavArrayBuffer(response) {
      if (!response.ok) {
        throw new Error(await responseErrorMessage(response));
      }
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('audio') && !contentType.includes('octet-stream')) {
        throw new Error(await responseErrorMessage(response));
      }

      const uploadId = response.headers.get('x-upload-id');
      const audioFormat = (response.headers.get('x-audio-format') || '').toLowerCase();
      const sampleRate = Number(response.headers.get('x-sample-rate') || 24000);
      const rawBuffer = await response.arrayBuffer();

      if (contentType.includes('application/octet-stream')) {
        const pcm = extractAudioPayloadFromFrames(rawBuffer);
        return {
          uploadId,
          wavArrayBuffer: pcm16ToWavArrayBuffer(pcm, sampleRate),
        };
      }

      if (contentType.includes('audio/pcm') || audioFormat === 'pcm_s16le') {
        return {
          uploadId,
          wavArrayBuffer: pcm16ToWavArrayBuffer(rawBuffer, sampleRate),
        };
      }

      return {
        uploadId,
        wavArrayBuffer: rawBuffer,
      };
    }

    async function waitForTtsFinalization(uploadId) {
      if (!uploadId) return null;
      for (let attempt = 0; attempt < 24; attempt++) {
        let item = null;
        try {
          item = await getTTSRequestByUploadId(uploadId);
        } catch (err) {
          if (attempt === 23) throw err;
          await sleep(500);
          continue;
        }
        if (item?.status === 'finished' && item.audio_file_id != null) {
          return item;
        }
        if (item?.status === 'failed') {
          throw new Error(item.reason || 'TTS request failed after streaming.');
        }
        await sleep(500);
      }
      throw new Error('TTS audio finished streaming, but final audio id was not available yet.');
    }

    async function runTtsPayload(payload) {
      const streaming = isStreamingModel(payload.model);
      let response;
      if (streaming) {
        const streamPayload = { ...payload };
        if (isIndexTTS2Model(streamPayload.model) && (streamPayload.voice_name || streamPayload.voice_id)) {
          streamPayload.text_chunks = Array.isArray(streamPayload.text_chunks) && streamPayload.text_chunks.length
            ? streamPayload.text_chunks
            : [streamPayload.text];
          response = await postNewTTSChunkStreamByVoiceRequest(streamPayload);
        } else {
          response = await postNewTTSStreamRequest(streamPayload);
        }
      } else {
        response = await postNewTTSRequest(payload);
      }

      if (!response) {
        throw new Error('Authentication required.');
      }
      const { uploadId, wavArrayBuffer } = await responseToWavArrayBuffer(response);
      let audioFileId = response.headers.get('x-audio-id');
      if (!audioFileId && uploadId) {
        const finalized = await waitForTtsFinalization(uploadId);
        audioFileId = finalized?.audio_file_id ?? null;
      }
      return { uploadId, audioFileId, wavArrayBuffer };
    }

    function showError(title, message, details = []) {
      infoTitle.value = title;
      infoMessage.value = message;
      infoDetails.value = details;
      showInfoPopup.value = true;
      showInfoPopupCancelButton.value = false;
    }

    function hasTextReviewIssues(reviewIssues) {
      return Boolean(reviewIssues?.unsupportedIssues?.length || reviewIssues?.longProLineIssues?.length);
    }

    function showTextReviewPrompt(reviewIssues, action) {
      const unsupportedIssues = reviewIssues?.unsupportedIssues || [];
      const longProLineIssues = reviewIssues?.longProLineIssues || [];
      const hasUnsupportedCharacters = unsupportedIssues.length > 0;
      const hasLongProLines = longProLineIssues.length > 0;
      const allReviewIssues = [...unsupportedIssues, ...longProLineIssues];
      const lineText = formatUnsupportedLineList(allReviewIssues);
      const isExportAction = action === 'export';
      const isPlayAllAction = action === 'playAll';
      const actionText = isExportAction ? 'export' : isPlayAllAction ? 'generate and play this page' : 'generate this line';
      const longProLineLimit = longProLineIssues[0]?.limit || 400;
      const longestProLineLength = hasLongProLines
        ? Math.max(...longProLineIssues.map(issue => issue.length))
        : 0;
      const longProLineCount = longProLineIssues.length;
      const longProLineVerb = longProLineCount === 1 ? 'is' : 'are';
      const unsupportedLineCount = new Set(unsupportedIssues.map(issue => issue.lineIndex)).size;
      const unsupportedLineText = unsupportedLineCount === 1 ? '1 line' : `${unsupportedLineCount} lines`;

      if (hasUnsupportedCharacters && hasLongProLines) {
        unsupportedCharacterTitle.value = 'Text Needs Review';
      } else if (hasLongProLines) {
        unsupportedCharacterTitle.value = 'Long Pro Lines';
      } else {
        unsupportedCharacterTitle.value = 'Unsupported Characters Found';
      }

      unsupportedCharacterMessage.value = `${lineText} needs review before you ${actionText}.`;
      unsupportedCharacterSummary.value = '';
      unsupportedCharacterReviewSections.value = [
        hasLongProLines
          ? {
            key: 'long-lines',
            kind: 'long-lines',
            title: 'Excessive long lines',
            countText: longProLineCount === 1 ? '1 Pro line' : `${longProLineCount} Pro lines`,
            verb: longProLineVerb,
            limitText: `${longProLineLimit}-character limit`,
            longestText: `${longestProLineLength} characters`,
          }
          : null,
        hasUnsupportedCharacters
          ? {
            key: 'unsupported-characters',
            kind: 'unsupported-characters',
            title: 'Unsupported characters',
            countText: unsupportedIssues.length === 1 ? '1 unsupported character' : `${unsupportedIssues.length} unsupported characters`,
            lineText: unsupportedLineText,
            mostCommon: summarizeMostCommonUnsupportedCharacters(unsupportedIssues),
          }
          : null,
      ].filter(Boolean);
      unsupportedCharacterDetails.value = [
        ...unsupportedIssues.map(formatUnsupportedCharacterIssueDetail),
        ...longProLineIssues.map(formatLongProLineIssueDetail),
      ];
      unsupportedCharacterConfirmText.value = hasLongProLines
        ? (isExportAction ? 'Export anyway' : isPlayAllAction ? 'Generate and play anyway' : 'Generate anyway')
        : isExportAction
          ? 'Export without those characters'
          : isPlayAllAction
            ? 'Generate and play without those characters'
            : 'Generate without those characters';
      unsupportedCharacterCancelText.value = isExportAction
        ? 'Cancel export'
        : isPlayAllAction
          ? 'Cancel'
          : 'Cancel generation';
      pendingUnsupportedCharacterAction.value = action;
      pendingUnsupportedCharacterLineIndex.value = allReviewIssues[0]?.lineIndex ?? editorSelectedLineIndex.value;
      showUnsupportedCharacterPopup.value = true;
    }

    function clearPendingUnsupportedCharacterAction() {
      pendingUnsupportedCharacterAction.value = null;
      pendingUnsupportedCharacterLineIndex.value = null;
      unsupportedCharacterTitle.value = 'Unsupported Characters Found';
      unsupportedCharacterMessage.value = '';
      unsupportedCharacterSummary.value = '';
      unsupportedCharacterReviewSections.value = [];
      unsupportedCharacterDetails.value = [];
      unsupportedCharacterConfirmText.value = 'Generate without those characters';
      unsupportedCharacterCancelText.value = 'Cancel generation';
    }

    async function confirmUnsupportedCharacterAction() {
      const action = pendingUnsupportedCharacterAction.value;
      const idx = pendingUnsupportedCharacterLineIndex.value;
      clearPendingUnsupportedCharacterAction();
      if (action === 'export') {
        await startExport({ ignoreUnsupportedCharacters: true, ignoreLongProLines: true });
        return;
      }
      if (action === 'playAll') {
        await playAllPageAudio({ ignoreUnsupportedCharacters: true, ignoreLongProLines: true });
        return;
      }
      if (idx == null) return;
      await generateSpeech({ lineIndex: idx, ignoreUnsupportedCharacters: true, ignoreLongProLines: true });
    }

    function openExportDialog() {
      showExport.value = true;
    }

    function normalizeAudioFileId(audioFileId) {
      const numeric = Number(audioFileId);
      return Number.isNaN(numeric) ? audioFileId : numeric;
    }

    function getCleanAudioId(audioId) {
      if (audioId === null || audioId === undefined) return '';
      const cleanAudioId = String(audioId).replace(/^"|"$/g, '').trim();
      if (!cleanAudioId || cleanAudioId === '-1') return '';
      if (cleanAudioId.toLowerCase() === 'null' || cleanAudioId.toLowerCase() === 'undefined') return '';
      return cleanAudioId;
    }

    function audioCacheRequest(audioId) {
      return new Request(`${window.location.origin}/tts-line-audio-cache/${encodeURIComponent(audioId)}`);
    }

    function rememberAudioBlob(audioId, blob) {
      if (!audioId || !(blob instanceof Blob)) return;
      const cacheKey = String(audioId);
      if (lineAudioBlobCache.has(cacheKey)) {
        lineAudioBlobCache.delete(cacheKey);
      }
      lineAudioBlobCache.set(cacheKey, blob);
      while (lineAudioBlobCache.size > TTS_LINE_AUDIO_MEMORY_LIMIT) {
        const oldestKey = lineAudioBlobCache.keys().next().value;
        lineAudioBlobCache.delete(oldestKey);
      }
    }

    async function getCachedLineAudioBlob(audioId) {
      const cacheKey = String(audioId);
      const memoryBlob = lineAudioBlobCache.get(cacheKey);
      if (memoryBlob) return memoryBlob;
      if (!window.caches) return null;
      try {
        const cache = await window.caches.open(TTS_LINE_AUDIO_CACHE_NAME);
        const response = await cache.match(audioCacheRequest(cacheKey));
        if (!response) return null;
        const blob = await response.blob();
        rememberAudioBlob(cacheKey, blob);
        return blob;
      } catch (err) {
        console.warn('Failed to read cached TTS audio:', err);
        return null;
      }
    }

    async function cacheLineAudioBlob(audioId, blob) {
      const cacheKey = String(audioId || '');
      if (!cacheKey || !(blob instanceof Blob)) return;
      rememberAudioBlob(cacheKey, blob);
      if (!window.caches) return;
      try {
        const cache = await window.caches.open(TTS_LINE_AUDIO_CACHE_NAME);
        await cache.put(audioCacheRequest(cacheKey), new Response(blob, {
          headers: {
            'Content-Type': blob.type || 'audio/wav',
            'Cache-Control': 'max-age=604800',
          },
        }));
      } catch (err) {
        console.warn('Failed to cache TTS audio:', err);
      }
    }

    async function getLineAudioBlob(audioId) {
      const cachedBlob = await getCachedLineAudioBlob(audioId);
      if (cachedBlob) return cachedBlob;

      const downloaded = await downloadFile(audioId);
      const blob = downloaded instanceof Blob
        ? downloaded
        : new Blob([downloaded], { type: 'audio/wav' });
      await cacheLineAudioBlob(audioId, blob);
      return blob;
    }

    function setBottomAudioSource(url, audioId = null) {
      if (generatedAudioObjectUrl && generatedAudioObjectUrl !== url) {
        URL.revokeObjectURL(generatedAudioObjectUrl);
      }
      generatedAudioObjectUrl = url && url.startsWith('blob:') ? url : '';
      generatedAudio.value = url || '';
      bottomPlayerAudioId.value = audioId ? String(audioId) : null;
      if (!url && audioPlayer.value) {
        audioPlayer.value.pause();
        audioPlayer.value.load();
      }
    }

    async function loadSelectedLineAudio(options = {}) {
      const { autoplay = false } = options;
      const requestId = selectedLineAudioRequestId + 1;
      selectedLineAudioRequestId = requestId;
      const idx = editorSelectedLineIndex.value;
      const audioId = selectedLineAudioId.value;

      if (!audioId) {
        isLoadingSelectedLineAudio.value = false;
        setBottomAudioSource('', null);
        return;
      }
      if (bottomPlayerAudioId.value === audioId && generatedAudio.value) {
        if (autoplay) {
          nextTick(() => {
            if (!isBottomPlayerDisabled.value) audioPlayer.value?.play();
          });
        }
        return;
      }

      const cachedAudioBlob = await getCachedLineAudioBlob(audioId);
      if (requestId !== selectedLineAudioRequestId || idx !== editorSelectedLineIndex.value) return;
      if (cachedAudioBlob) {
        setBottomAudioSource(URL.createObjectURL(cachedAudioBlob), audioId);
        if (autoplay) {
          nextTick(() => {
            if (!isBottomPlayerDisabled.value) audioPlayer.value?.play();
          });
        }
        return;
      }

      isLoadingSelectedLineAudio.value = true;
      setBottomAudioSource('', null);
      try {
        const audioBlob = await getLineAudioBlob(audioId);
        if (requestId !== selectedLineAudioRequestId || idx !== editorSelectedLineIndex.value) return;

        setBottomAudioSource(URL.createObjectURL(audioBlob), audioId);
        if (autoplay) {
          nextTick(() => {
            if (!isBottomPlayerDisabled.value) audioPlayer.value?.play();
          });
        }
      } catch (err) {
        if (requestId === selectedLineAudioRequestId) {
          setBottomAudioSource('', null);
          showError('Error!', 'Download Speech error: ' + err);
        }
      } finally {
        if (requestId === selectedLineAudioRequestId) {
          isLoadingSelectedLineAudio.value = false;
        }
      }
    }

    function getLineReadableText(line) {
      if (!line || line.text === 'NULL') return '';
      return String(line.text || '');
    }

    function getReadableLineIndexes() {
      return (Array.isArray(pageLines.value) ? pageLines.value : [])
        .map((line, idx) => ({ line, idx }))
        .filter(({ line }) => getLineReadableText(line).trim() !== '')
        .map(({ idx }) => idx);
    }

    async function onPlayAllLines() {
      await playAllPageAudio();
    }

    async function playAllPageAudio(options = {}) {
      const ignoreUnsupportedCharacters = options?.ignoreUnsupportedCharacters === true;
      const ignoreLongProLines = options?.ignoreLongProLines === true;
      if (isExporting.value) {
        showError('Please wait', 'Export is still running. Please wait for it to finish before playing this page.');
        return;
      }
      if (generatingLineIndex.value !== null) {
        showError('Please wait', `Line ${generatingLineIndex.value + 1} is still generating. Please wait for it to finish before playing this page.`);
        return;
      }

      const lineIndexes = getReadableLineIndexes();
      if (!lineIndexes.length) {
        showError('No readable text', 'This page has no text lines to generate or play.');
        return;
      }

      const reviewIssues = {
        unsupportedIssues: ignoreUnsupportedCharacters ? [] : findUnsupportedCharacterIssues(pageLines.value),
        longProLineIssues: ignoreLongProLines ? [] : findLongProLineIssues(pageLines.value, model.value),
      };
      if (hasTextReviewIssues(reviewIssues)) {
        showTextReviewPrompt(reviewIssues, 'playAll');
        return;
      }

      const pageIdSnapshot = selectedPage.value;
      const audioBuffers = [];
      selectedLineAudioRequestId += 1;
      isLoadingSelectedLineAudio.value = true;
      setBottomAudioSource('', null);

      try {
        for (const idx of lineIndexes) {
          if (selectedPage.value !== pageIdSnapshot) return;
          const originalLineText = getLineReadableText(pageLines.value[idx]);
          const playableText = ignoreUnsupportedCharacters
            ? removeUnsupportedTtsCharacters(originalLineText)
            : originalLineText;
          if (!playableText.trim()) continue;

          if (!getCleanAudioId(pageLines.value[idx]?.audio_file_id)) {
            editorSelectedLineIndex.value = idx;
            editorSelectedLine.value = pageLines.value[idx]?.text || '';
            await generateSpeech({
              lineIndex: idx,
              ignoreUnsupportedCharacters,
              ignoreLongProLines,
              autoplay: false,
            });
          }

          const audioId = getCleanAudioId(pageLines.value[idx]?.audio_file_id);
          if (!audioId) return;
          const audioBlob = await getLineAudioBlob(audioId);
          audioBuffers.push(await audioBlob.arrayBuffer());
        }

        if (!audioBuffers.length) {
          showError('No playable audio', 'No readable audio was available for this page.');
          return;
        }

        const mergedAB = mergeWavs(audioBuffers);
        const mergedBlob = new Blob([mergedAB], { type: 'audio/wav' });
        const url = URL.createObjectURL(mergedBlob);
        setBottomAudioSource(url, TTS_PAGE_PLAYBACK_AUDIO_ID);
        nextTick(() => {
          if (!isBottomPlayerDisabled.value) audioPlayer.value?.play();
        });
      } catch (err) {
        showError('Error!', 'Play all error: ' + err);
      } finally {
        isLoadingSelectedLineAudio.value = false;
        loadBalance();
      }
    }

    async function startExport(options = {}) {
      const ignoreUnsupportedCharacters = options?.ignoreUnsupportedCharacters === true;
      const ignoreLongProLines = options?.ignoreLongProLines === true;
      if (!pageLines.value || pageLines.value.length === 0) {
        return;
      }
      const reviewIssues = {
        unsupportedIssues: ignoreUnsupportedCharacters ? [] : findUnsupportedCharacterIssues(pageLines.value),
        longProLineIssues: ignoreLongProLines ? [] : findLongProLineIssues(pageLines.value, model.value),
      };
      if (hasTextReviewIssues(reviewIssues)) {
        showTextReviewPrompt(reviewIssues, 'export');
        return;
      }
      try {
        isGenerating.value = true;
        isExporting.value = true;
        const audioBuffers = [];
        for (let idx = 0; idx < pageLines.value.length; idx++) {
          const l = pageLines.value[idx];
          const originalLineText = l.text === 'NULL' ? '' : String(l.text || '');
          if (!originalLineText.trim()) continue;
          const exportLineText = ignoreUnsupportedCharacters
            ? removeUnsupportedTtsCharacters(originalLineText)
            : originalLineText;
          if (!exportLineText.trim()) continue;
          if (l.audio_file_id != -1) {
            const cleanAudioId = typeof l.audio_file_id === 'string'
              ? l.audio_file_id.replace(/^"|"$/g, '')
              : l.audio_file_id;
            const data = await downloadFile(cleanAudioId);
            const existingArrayBuffer = data instanceof Blob ? await data.arrayBuffer() : data;
            audioBuffers.push(existingArrayBuffer);
            continue;
          }
          const rawVs = typeof l.voice_setting === 'string'
            ? cleanJSON(l.voice_setting)
            : (l.voice_setting || {});
          const vs = (rawVs && Object.keys(rawVs).length > 0)
            ? rawVs
            : getDefaultConfigForModel(model.value);

          let payload = {
            model: model.value,
            text: exportLineText,
            ...vs
          };
          payload = await formatTTSPayload(payload)
          const payloadCheckingResult = checkTTSPayload(payload);
          if (!payloadCheckingResult.status) {
            showError('Error!', 'TTS payload check failed: ' + payloadCheckingResult.returnMessage);
            return;
          }
          const result = await runTtsPayload(payload);
          if (result.audioFileId) {
            pageLines.value[idx].audio_file_id = normalizeAudioFileId(result.audioFileId);
          }
          audioBuffers.push(result.wavArrayBuffer);
        }
        if (audioBuffers.length) {
          const mergedAB = mergeWavs(audioBuffers);
          const mergedBlob = new Blob([mergedAB], { type: 'audio/wav' });
          const url = URL.createObjectURL(mergedBlob);

          const a = document.createElement('a');
          a.href = url;
          a.download = `export_page_${selectedPageName.value}.wav`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
      } catch (err) {
        showError('Error!', err);
      } finally {
        await saveLines();
        isGenerating.value = false;
        isExporting.value = false;
        await loadSelectedLineAudio();
        loadBalance();
      }
      closeExport();
    }

    const clickedLineNumber = ref(null);

    const gridStyle = computed(() => {
      let cols;
      if (showProjectPanel.value && showSettingsPanel.value) {
        cols = '1fr 2fr 1fr';   // project | editor | settings
      } else if (!showProjectPanel.value && showSettingsPanel.value) {
        cols = '3fr 1fr';       // editor | settings
      } else if (showProjectPanel.value && !showSettingsPanel.value) {
        cols = '1fr 3fr';       // project | editor
      } else {
        cols = '1fr';           // editor only
      }
      return { gridTemplateColumns: cols };
    });

    async function handleLineNumberClick(num) {
      const idx = num - 1; // convert to 0-based index
      clickedLineNumber.value = idx;
      const line = pageLines.value[idx];
      if (line) {
        try {
          pageLines.value[idx].audio_file_id = -1;
          audioFileIds.value.splice(idx, 1, -1); // ensure reactivity on array change
          pageLines.value[idx].status = 'updated';
          await updateLineByUserId(
            project.project_id,
            selectedPage.value,
            line.line_id,
            line.text,
            -1,
            line.voice_setting ? cleanJSON(line.voice_setting) : {}
          );
        } catch (e) {
          console.error('updateLineByUserId failed:', e);
        }
      }
    }

    function toggleProjectPanel() {
      showProjectPanel.value = !showProjectPanel.value;
    }
    function toggleSettingsPanel() {
      showSettingsPanel.value = !showSettingsPanel.value;
    }

    function scheduleAutoSave() {
      if (autosaveTimer) clearTimeout(autosaveTimer);
      autosaveTimer = setTimeout(() => {
        saveLines();
      }, 5000);
    }

    async function selectPage(arg1, arg2) {
      let id, name;
      if (typeof arg1 === 'object' && arg1 !== null) {
        id = arg1.id;
        name = arg1.name;
      } else {
        id = arg1;
        name = arg2;
      }

      if (!name) {
        const pagesSrc = projectPanel.value?.pages || [];
        const findName = (nodes, target) => {
          for (const n of nodes) {
            if (n.id === target) return n.name;
            if (n.children && n.children.length) {
              const r = findName(n.children, target);
              if (r) return r;
            }
          }
          return '';
        };
        name = findName(pagesSrc, id);
      }

      selectedPage.value = id;
      selectedPageName.value = name || '';
      editorSelectedLineIndex.value = 0;
      editorSelectedLine.value = '';
      selectedLineAudioRequestId += 1;
      isLoadingSelectedLineAudio.value = false;
      setBottomAudioSource('', null);
      await loadLines();
    }
    function onPageChanged() {
      projectPanel.value?.loadPages();
    }
    function getVoiceSetting(line) {
      if (!line?.voice_setting) return null;
      try {
        return typeof line.voice_setting === 'string'
          ? cleanJSON(line.voice_setting)
          : line.voice_setting;
      } catch (err) {
        console.warn('Failed to parse voice_setting for line', line, err);
        return null;
      }
    }

    function applySettingsFromLine(idx) {
      const line = pageLines.value[idx];
      const vs = getVoiceSetting(line);
      if (!vs) {
        console.warn('Invalid voice_setting, using defaults');
        model.value = defaultModel.value;
        settingsConfig.value = { ...getDefaultConfigForModel(defaultModel.value) };
        ttsSharedData.value.voice_name = 'Default';
        return;
      }
      if (vs.model) {
        model.value = vs.model;
        const { model: _m, ...rest } = vs;
        settingsConfig.value = { ...getDefaultConfigForModel(vs.model), ...rest };
        ttsSharedData.value = {
          ...ttsSharedData.value,
          voice_name: vs.voice_name || 'Default',
          audio_file_id: vs.audio_file_id,
          voice_instruct_text: vs.voice_instruct_text || '',
        };
      } else {
        model.value = defaultModel.value;
        settingsConfig.value = { ...getDefaultConfigForModel(defaultModel.value) };
        ttsSharedData.value.voice_name = 'Default';
      }
    }
    async function loadLines() {
      isLoadingLines.value = true;
      if (autosaveTimer) clearTimeout(autosaveTimer);
      selectedLineAudioRequestId += 1;
      isLoadingSelectedLineAudio.value = false;
      setBottomAudioSource('', null);
      try {
        const { userId } = checkAuthentication();
        const allLines = await getLinesByUserId(userId);
        if (autosaveTimer) clearTimeout(autosaveTimer);
        pageLines.value = allLines.filter(
          l => Number(l.page_id) === selectedPage.value && l.status !== 'deleted'
        );
        audioFileIds.value = pageLines.value.map(l => l.audio_file_id ?? -1);

        const textInput = pageLines.value
          .slice()
          .sort((a, b) => a.line_id - b.line_id)
          .map(l => (l.text === null || l.text === 'NULL') ? '' : l.text)
          .join('\n');
        editorContent.value = textInput;
        if (pageLines.value.length > 0) {
          if (
            editorSelectedLineIndex.value < 0
            || editorSelectedLineIndex.value >= pageLines.value.length
          ) {
            editorSelectedLineIndex.value = 0;
          }
          editorSelectedLine.value = pageLines.value[editorSelectedLineIndex.value]?.text || '';
          applySettingsFromLine(editorSelectedLineIndex.value);
          await loadSelectedLineAudio();
        } else {
          editorSelectedLineIndex.value = 0;
          editorSelectedLine.value = '';
          setBottomAudioSource('', null);
        }
      } catch (err) {
        console.error('Failed to load lines by user:', err);
      } finally {
        isLoadingLines.value = false;
        if (autosaveTimer) clearTimeout(autosaveTimer);
        saveStatus.value = 'Saved';
      }
    }

    function cleanJSON(dirty) {
      let val = dirty.trim();
      try {
        while (typeof val === 'string') {
          val = JSON.parse(val);
        }
        return val;
      } catch (err) {
        console.error('cleanJSON failed:', err);
        return null;
      }
    }

    function findDataChunk(view) {
      let off = 12;
      while (off + 8 <= view.byteLength) {
        const id = String.fromCharCode(
          view.getUint8(off),
          view.getUint8(off + 1),
          view.getUint8(off + 2),
          view.getUint8(off + 3)
        );
        const size = view.getUint32(off + 4, true);
        if (id === 'data') return { dataOffset: off + 8, dataSize: size, sizeFieldOffset: off + 4 };
        off += 8 + size;
      }
      throw new Error('WAV data chunk not found');
    }

    function mergeWavs(buffers) {
      if (!buffers.length) return new ArrayBuffer(0);
      const firstV = new DataView(buffers[0]);
      const firstInfo = findDataChunk(firstV);
      const headerLen = firstInfo.dataOffset;

      let total = 0;
      const chunks = buffers.map(b => {
        const v = new DataView(b);
        const info = findDataChunk(v);
        total += info.dataSize;
        return { b, info };
      });

      const out = new ArrayBuffer(headerLen + total);
      const outV = new DataView(out);

      // copy header
      for (let i = 0; i < headerLen; i++) outV.setUint8(i, firstV.getUint8(i));
      // fix sizes
      outV.setUint32(4, out.byteLength - 8, true);                  // RIFF size
      outV.setUint32(firstInfo.sizeFieldOffset, total, true);       // data chunk size

      let write = headerLen;
      chunks.forEach(({ b, info }) => {
        const src = new Uint8Array(b, info.dataOffset, info.dataSize);
        new Uint8Array(out, write, info.dataSize).set(src);
        write += info.dataSize;
      });
      return out;
    }

    async function saveLines() {
      // If no project or page selected, skip save
      if (!project.project_id || !selectedPage.value) {
        saveStatus.value = 'Saved';
        return;
      }
      saveStatus.value = 'Saving';
      const projectId = project.project_id;
      const pageId = selectedPage.value;

      function sanitizeLines(lines) {
        return lines.map(l => {
          let vsObj = {};
          if (l.voice_setting) {
            try {
              vsObj = cleanJSON(l.voice_setting);
            } catch (e) {
              console.warn('sanitizeLines: invalid voice_setting for line', l, e);
            }
          }
          return {
            lineId: l.line_id ?? null,
            text: l.text,
            status: l.status,
            ...(l.audio_file_id != null ? { audioFileId: l.audio_file_id } : {}),
            voiceSettings: vsObj
          };
        });
      }

      const pageLinesArray = sanitizeLines(pageLines.value || []);
      const params = {
        projectId,
        pageId,
        lines: pageLinesArray
      };
      console.log(params);
      const _response = await updateLinesByPage(params);
      saveStatus.value = 'Saved';
    }
    async function generateSpeech(options = {}) {
      const idx = options.lineIndex ?? editorSelectedLineIndex.value;
      const ignoreUnsupportedCharacters = options.ignoreUnsupportedCharacters === true;
      const ignoreLongProLines = options.ignoreLongProLines === true;
      const shouldAutoplay = options.autoplay !== false;
      const line = pageLines.value[idx];
      if (isExporting.value) {
        showError('Please wait', 'Export is still running. Please wait for it to finish before generating a line.');
        return false;
      }
      if (generatingLineIndex.value !== null) {
        showError('Please wait', `Line ${generatingLineIndex.value + 1} is still generating. Please wait for it to finish before starting another line.`);
        return false;
      }
      if (!line) {
        showError('Error!', 'No line selected');
        return false;
      }
      const originalLineText = line.text === 'NULL' ? '' : String(line.text || '');
      const reviewIssues = {
        unsupportedIssues: ignoreUnsupportedCharacters
          ? []
          : findUnsupportedCharacterIssues([{ text: originalLineText }]).map(issue => ({ ...issue, lineIndex: idx })),
        longProLineIssues: ignoreLongProLines
          ? []
          : findLongProLineIssues([{ text: originalLineText }], model.value).map(issue => ({ ...issue, lineIndex: idx })),
      };
      if (hasTextReviewIssues(reviewIssues)) {
        showTextReviewPrompt(reviewIssues, 'generate');
        return false;
      }

      const lineText = ignoreUnsupportedCharacters
        ? removeUnsupportedTtsCharacters(originalLineText)
        : originalLineText;
      if (!lineText.trim()) {
        showError(
          'No readable text',
          ignoreUnsupportedCharacters
            ? 'After skipping characters the voice engine cannot read, this line has no text left to generate.'
            : 'No line selected'
        );
        return false;
      }

      const currentVoiceSetting = clonePlain(getVoiceSetting(line) || {}) || {};
      const settingsSnapshot = clonePlain(settingsConfig.value) || {};
      const modelSnapshot = model.value;
      const mergedVoiceSetting = {
        ...currentVoiceSetting,
        ...settingsSnapshot,
        model: modelSnapshot,
      };
      const requestId = generationRequestId + 1;
      const pageIdSnapshot = selectedPage.value;
      const lineIdSnapshot = line.line_id ?? null;
      const lineRef = line;
      const isCurrentLineTarget = () => {
        const currentLine = pageLines.value[idx];
        return selectedPage.value === pageIdSnapshot
          && currentLine
          && (
            currentLine === lineRef
            || (lineIdSnapshot != null && currentLine.line_id === lineIdSnapshot)
            || (lineIdSnapshot == null && currentLine.text === originalLineText)
          );
      };
      generationRequestId = requestId;
      isGenerating.value = true;
      generatingLineIndex.value = idx;
      generatingPageId.value = pageIdSnapshot;
      if (autosaveTimer) clearTimeout(autosaveTimer);
      try {
        if (!await requireAuth('TTSView.generateSpeech')) return false;
        if (!isCurrentLineTarget()) return false;

        pageLines.value[idx].voice_setting = JSON.stringify(mergedVoiceSetting);
        await saveLines();
        if (!isCurrentLineTarget()) return false;
        let payload = {
          ...settingsSnapshot,
          ...mergedVoiceSetting,
          "model": modelSnapshot,
          "text": lineText,
        }
        payload = await formatTTSPayload(payload)
        const payloadCheckingResult = checkTTSPayload(payload);
        if (!payloadCheckingResult.status) {
          showError('Error!', 'TTS payload check failed: ' + payloadCheckingResult.returnMessage);
          return false;
        }

        const result = await withTimeout(
          runTtsPayload(payload),
          TTS_GENERATION_TIMEOUT_MS,
          'Generation took too long and was stopped. Please try again.'
        );
        if (requestId !== generationRequestId) return false;

        const isSameLine = isCurrentLineTarget();
        if (isSameLine && result.audioFileId) {
          pageLines.value[idx].audio_file_id = normalizeAudioFileId(result.audioFileId);
        }

        const wavBlob = new Blob([result.wavArrayBuffer], { type: 'audio/wav' });
        const audioUrl = URL.createObjectURL(wavBlob);
        const cleanResultAudioId = getCleanAudioId(result.audioFileId);
        if (cleanResultAudioId) {
          await cacheLineAudioBlob(cleanResultAudioId, wavBlob);
        }

        if (shouldAutoplay) {
          setBottomAudioSource(audioUrl, cleanResultAudioId);
          nextTick(() => {
            if (!isBottomPlayerDisabled.value) audioPlayer.value.play();
          });
        } else {
          URL.revokeObjectURL(audioUrl);
        }
        if (isSameLine) {
          await saveLines();
        }
        return true;

      } catch (err) {
        if (requestId === generationRequestId) {
          showError('Error!', 'Generate Speech error: ' + err);
        }
        return false;
      } finally {
        if (requestId === generationRequestId) {
          isGenerating.value = false;
          generatingLineIndex.value = null;
          generatingPageId.value = null;
          if (shouldAutoplay) {
            await loadSelectedLineAudio();
          }
          loadBalance();
        }
      }
    }
    function downloadAudio() {
      if (isBottomPlayerDisabled.value || !generatedAudio.value) {
        return;
      }
      const link = document.createElement('a');
      link.href = generatedAudio.value;
      link.download = 'speech.wav';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    async function reloadAll() {
      editorContent.value = '';
      pageLines.value = [];
      selectedPage.value = 0;
      saveStatus.value = 'Saved';
      isLoadingSelectedLineAudio.value = false;
      setBottomAudioSource('', null);

      await projectPanel.value?.loadPages();
      await loadLines();
    }
    onMounted(async () => {
      await loadLines();
    });
    onBeforeUnmount(() => {
      selectedLineAudioRequestId += 1;
      if (generatedAudioObjectUrl) {
        URL.revokeObjectURL(generatedAudioObjectUrl);
        generatedAudioObjectUrl = '';
      }
    });
    watch(
      settingsConfig,
      async (newConfig, _oldConfig) => {
        const idx = editorSelectedLineIndex.value;
        if (idx != null && pageLines.value[idx]) {
          const current = getVoiceSetting(pageLines.value[idx]) || {};
          const merged = {
            model: model.value,
            ...current,
            ...newConfig,
          };
          pageLines.value[idx].voice_setting = JSON.stringify(merged);
        }
      },
      { deep: true }
    );
    watch(() => ttsSharedData.value.voice_name, (newVoiceName) => {
      const idx = editorSelectedLineIndex.value;
      if (idx != null && pageLines.value[idx]) {
        const vs = getVoiceSetting(pageLines.value[idx]) || {};
        vs.voice_name = newVoiceName;
        pageLines.value[idx].voice_setting = JSON.stringify(vs);
      }
    });
    watch(() => ttsSharedData.value.audio_file_id, (val) => {
      const idx = editorSelectedLineIndex.value;
      if (idx != null && pageLines.value[idx]) {
        const vs = getVoiceSetting(pageLines.value[idx]) || {};
        vs.audio_file_id = val;
        pageLines.value[idx].voice_setting = JSON.stringify(vs);
      }
    });
    watch(() => ttsSharedData.value.voice_instruct_text, (val) => {
      const idx = editorSelectedLineIndex.value;
      if (idx != null && pageLines.value[idx]) {
        const vs = getVoiceSetting(pageLines.value[idx]) || {};
        vs.voice_instruct_text = val;
        pageLines.value[idx].voice_setting = JSON.stringify(vs);
      }
    });
    watch(pageLines, () => {
      if (isGenerating.value || isLoadingLines.value || isExporting.value) return;
      saveStatus.value = 'Unsaved';
      scheduleAutoSave();
    }, { deep: true });


    watch(model, (newValue) => {
      const idx = editorSelectedLineIndex.value;
      if (idx != null && pageLines.value[idx]) {
        const currentSetting = getVoiceSetting(pageLines.value[idx]);
        if (!currentSetting || currentSetting.model !== newValue) {
          const defaultSettings = getDefaultConfigForModel(newValue);
          const newVoiceSetting = {
            model: newValue,
            ...defaultSettings,
          };
          pageLines.value[idx].voice_setting = JSON.stringify(newVoiceSetting);
          settingsConfig.value = { ...defaultSettings };
        }
      }
    });

    watch(editorSelectedLineIndex, (newLine) => {
      applySettingsFromLine(newLine);
    });

    watch(selectedLineAudioId, () => {
      if (isGenerating.value || isExporting.value || isLoadingLines.value) return;
      loadSelectedLineAudio();
    });

    watch(showImportConfirmPopup, (isOpen) => {
      if (!isOpen) {
        clearPendingImport();
      }
    });

    watch(showUnsupportedCharacterPopup, (isOpen) => {
      if (!isOpen) {
        clearPendingUnsupportedCharacterAction();
      }
    });

    return {
      reloadAll,
      selectedPage,
      selectedPageName,
      selectPage,
      project,
      activeTab,
      settingsConfig,
      pageLines,
      audioFileIds,
      model,
      projectPanel,
      audioPlayer,
      onPageChanged,
      editorContent,
      editorSelectedLine,
      editorSelectedLineIndex,
      saveLines,
      generateSpeech,
      generatedAudio,
      saveStatus,
      downloadAudio,
      isLoadingLines,
      showProjectPanel,
      showSettingsPanel,
      gridStyle,
      toggleProjectPanel,
      toggleSettingsPanel,
      clickedLineNumber,
      handleLineNumberClick,
      showExport,
      closeExport,
      startExport,
      openExportDialog,
      onLineSelect,
      onLinePlay,
      onPlayAllLines,
      isGenerating,
      isEditorLineGenerating,
      generatingLineIndex,
      isExporting,
      isLoadingSelectedLineAudio,
      isBottomPlayerDisabled,
      importInput,
      triggerImport,
      onImportFile,
      showImportConfirmPopup,
      pendingImportFileName,
      ignoreEmptyImportLines,
      confirmImportFile,
      clearPendingImport,
      showUnsupportedCharacterPopup,
      unsupportedCharacterTitle,
      unsupportedCharacterMessage,
      unsupportedCharacterSummary,
      unsupportedCharacterReviewSections,
      unsupportedCharacterDetails,
      unsupportedCharacterConfirmText,
      unsupportedCharacterCancelText,
      confirmUnsupportedCharacterAction,
      clearPendingUnsupportedCharacterAction,
      balance,
      showInfoPopup,
      infoTitle,
      infoMessage,
      infoDetails,
      showInfoPopupCancelButton,
      goToProjects,
    };
  },
  async created() {
    const qp = this.$route.query.project;
    if (qp) {
      try {
        const parsed = JSON.parse(decodeURIComponent(qp));
        this.project.project_id = parsed.project_id;
        this.project.project_name = parsed.project_name;
      } catch (e) {
        console.error('Invalid project data in query:', e);
      }
    }

  }
};
</script>

<style scoped>
.main-content {
  background-color: var(--background-white);
  flex: 1 1 auto;
  overflow: hidden;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.content-card {
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  padding: 1.5rem;
}

.info-message p {
  margin: 0;
}

.info-detail-list {
  margin: 0.75rem 0 0;
  padding-left: 1.25rem;
  color: var(--text-secondary);
  line-height: 1.5;
}

.info-detail-list li {
  margin: 0.25rem 0;
}

.header {
  flex: 0 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header h3 {
  margin: 0;
  font-weight: 600;
}

.panel-toggle-bar {
  flex: 0 0 auto;
  display: flex;
  margin: 8px 0px 0px;
}


.panel-toggle-item {
  padding: 5px 10px 5px;
  margin: 0px 5px 5px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.8rem;
  color: var(--text-secondary);
  border-radius: 4px;
}

.panel-toggle-item:hover {
  background: var(--background-white-hover, #f5f5f7);
}

.panel-toggle-item input {
  display: none;
}

.slider {
  position: relative;
  width: 44px;
  height: 24px;
  background: #d1d5db;
  border-radius: 12px;
  transition: background .2s;
  flex-shrink: 0;
}

.slider::after {
  content: "";
  position: absolute;
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  background: #fff;
  border-radius: 50%;
  transition: transform .2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, .25);
}

input:checked+.slider {
  background: var(--purple)
}

input:checked+.slider::after {
  transform: translateX(20px);
}

.switch-label {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-weight: 500;
  color: #4a4a6a;
}

.toggle-btn {
  padding: 5px 10px 5px;
  margin: 0px 5px 5px;
  font-size: 0.8rem;
  border: none;
  background: none;
  border-radius: 4px;
  cursor: pointer;
  color: var(--text-secondary);
}

.toggle-btn:hover {
  background: var(--background-white-hover);
  color: var(--text-primary);
}

.credit-meter {
  padding: 5px 10px 5px;
  margin: 0px 5px 5px;
  font-size: 0.8rem;
  border: none;
  background: none;
  border-radius: 4px;
  cursor: default;
  background: var(--background-white);
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.3rem;
}

.credit-count {
  color: var(--purple);
}

.spacer {
  flex: 1 1 auto;
}

.panels-container {
  flex: 1 1 auto;
  display: grid;
  grid-template-columns: 1fr 3fr 1fr;
  gap: 0;
  min-height: 0;
  height: 100%;
  overflow: hidden;
  position: relative;
}

.project-panel {
  flex: 1 1 auto;
  overflow: hidden;
  border: 1px solid var(--border-color);
  background-color: var(--background-white);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-height: 0;
  height: 100%;
}

.editor-panel {
  flex: 1 1 auto;
  min-height: 0;
  border-top: 1px solid var(--border-color);
  border-bottom: 1px solid var(--border-color);
  background-color: var(--background-white);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-width: 0;
}

.settings-history-panel {
  flex: 0 0 auto;
  border: 1px solid var(--border-color);
  background-color: var(--background-white);
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-height: 0;
  height: 100;
}

.settings-history-tabs {
  display: flex;
  gap: 1rem;
  border-bottom: 1px solid var(--border-color);
}

.tab-btn {
  border: none;
  background: none;
  padding: 0.5rem 0.25rem;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;
  color: var(--text-secondary);
  position: relative;
}

.tab-btn.active {
  color: var(--purple);
}

.tab-btn.active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  right: 0;
  height: 2px;
  background-color: var(--purple);
}

.audio-player-wrapper {
  flex: 0 0 auto;
  bottom: 0;
  background-color: var(--background-white);
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border-color);
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.audio-player-area {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
}

.audio-player-wrapper audio {
  width: 100%;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
  border-radius: 4px;
}

.audio-player-wrapper.disabled {
  opacity: 0.55;
}

.audio-player-wrapper.disabled audio {
  filter: grayscale(1);
  pointer-events: none;
}

.audio-player-wrapper.disabled .btn-download {
  cursor: not-allowed;
  pointer-events: none;
}

.audio-loading-state {
  flex: 1 1 auto;
  min-height: 54px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 0 1.25rem;
  border-radius: 8px;
  color: var(--text-secondary);
  background: #f7f8fb;
  font-weight: 500;
}

.audio-player-wrapper.loading {
  gap: 0;
}

.audio-loading-spinner {
  width: 20px;
  height: 20px;
  border: 3px solid #f0e5ff;
  border-top-color: var(--purple);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.btn {
  font-family: 'Poppins', sans-serif;
  text-decoration: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
  display: inline-block;
  border: none;
  cursor: pointer;
}

audio {
  flex-grow: 1;
  height: 50px;
}

.btn-download {
  background-color: transparent;
  color: black;
}

.btn-download:disabled {
  color: var(--text-secondary);
  cursor: not-allowed;
  opacity: 0.6;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid var(--purple);
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.editor-panel-inner {
  height: 100%;
}

.editor-panel-inner.disabled {
  pointer-events: none;
  opacity: 0.5;
}


.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-window {
  background: #fff;
  padding: 1.5rem 2rem;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  max-width: 500px;
  width: 100%;
  text-align: center;
}

.import-btn {
  font-weight: 500;
  margin-right: 8px;
}

.import-confirmation {
  display: flex;
  flex-direction: column;
  gap: 14px;
  color: var(--text-primary);
}

.import-confirmation p {
  margin: 0;
  line-height: 1.5;
}

.import-empty-line-option {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 0.95rem;
  color: var(--text-secondary);
}

.import-empty-line-option input {
  width: 16px;
  height: 16px;
  accent-color: var(--purple);
}

.export-btn {
  font-weight: 500;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  line-height: 1;
  padding: 2px 6px;
}

.modal-body {
  text-align: left;
  font-size: 0.95rem;
  color: var(--text-secondary);
  margin-bottom: 1.25rem;
}

.modal-warning {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: #fff7e6;
  border: 1px solid #ffdf9a;
  color: #8a6100;
  padding: 10px 12px;
  border-radius: 6px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  line-height: 1.4;
}

.warn-icon {
  display: inline-block;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #ffb02e;
  color: #fff;
  font-weight: 700;
  text-align: center;
  line-height: 18px;
  font-size: 0.9rem;
  flex: 0 0 18px;
}

.green-ln {
  color: #16a34a;
  font-weight: 600;
}

.modal-list {
  padding-left: 1.2rem;
  margin: 0.5rem 0 0;
  list-style: disc;
}

.modal-audio-tag {
  min-height: 30px;
  padding: 4px 6px 4px 10px;
  border: 1px solid #e5c3c8;
  border-radius: 999px;
  background: #fffafa;
  color: #8a3942;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: 'Inter', sans-serif;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1;
  vertical-align: middle;
}

.modal-audio-remove {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #8a3942;
  background: #f7e7e9;
}

.modal-audio-remove svg {
  width: 12px;
  height: 12px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

.btn-primary {
  background-color: var(--purple);
  color: #fff;
}

.btn-secondary {
  background-color: #f3f3f4;
  color: var(--text-primary);
}

.button-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #f3f3f4;
  border-top: 2px solid #fff;
  border-style: solid;
  border-color: #fff transparent transparent transparent;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
